import { useEffect, useMemo, useState } from 'react'
import Modal from 'react-modal'
import {
  CreateNotificationPresetRequestDto,
  NotificationPresetDto,
  NotificationPresetTrigger,
  NotificationPresetVariant,
  NotificationPresetVenuePresence,
  NOTIFICATION_PRESET_DEFAULT_NOTIFICATION_TYPE,
  NOTIFICATION_PRESET_DEFAULT_DEEP_LINK_TARGETS,
  UpdateNotificationPresetRequestDto,
  getNotificationPresetTemplateVariableHelp,
  getNotificationPresetNCountDescription,
  variantUsesNotificationPresetNCount,
  isEventNotificationPresetTrigger,
  isImmediateNotificationPresetTrigger,
  isScheduledNotificationPresetTrigger,
  isVenueNotificationPresetTrigger,
  isBatchNotificationPresetTrigger,
} from '@ivy-monorepo/shared'
import { adminService } from '../../services/admin.service'
import { BaseModalProps } from '../../types/modal'
import TextInput from '../ui/TextInput'

interface NotificationPresetModalProps extends BaseModalProps {
  preset: NotificationPresetDto | null
  meta: {
    triggers: NotificationPresetTrigger[]
    variants: NotificationPresetVariant[]
    triggerLabels: Record<string, string>
    variantLabels: Record<string, string>
    venuePresenceValues?: NotificationPresetVenuePresence[]
    venuePresenceLabels?: Record<string, string>
    templateVariables: Record<string, string[]>
    templateVariablesByTrigger?: Record<string, string[]>
    defaultNotificationTypes?: Record<string, string>
    onboardingVariants?: NotificationPresetVariant[]
    venueVariants?: NotificationPresetVariant[]
    conversationFollowUpVariants?: NotificationPresetVariant[]
    checkoutTypes?: string[]
    checkoutTypeLabels?: Record<string, string>
    deepLinkTargets?: string[]
    deepLinkTargetLabels?: Record<string, string>
  } | null
  onSaved: (preset: NotificationPresetDto) => void
  onDeleted?: (publicId: string) => void
}

function formatDelay(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} min`
  }
  if (minutes < 1440) {
    return `${Math.round(minutes / 60)} hr`
  }
  return `${Math.round(minutes / 1440)} day(s)`
}

const FALLBACK_ONBOARDING_VARIANTS = [
  NotificationPresetVariant.LiveCheckins,
  NotificationPresetVariant.LiveViews,
  NotificationPresetVariant.Breadcrumbs,
  NotificationPresetVariant.VisitPlans,
  NotificationPresetVariant.Events,
]

const FALLBACK_VENUE_VARIANTS = [
  NotificationPresetVariant.Default,
  NotificationPresetVariant.PeopleHere,
  NotificationPresetVariant.MatchingUsersHere,
  NotificationPresetVariant.MutualConnectionsHere,
  NotificationPresetVariant.RecentViewsHere,
  NotificationPresetVariant.Breadcrumbs,
  NotificationPresetVariant.VisitPlans,
  NotificationPresetVariant.Events,
]

const FALLBACK_CONVERSATION_FOLLOWUP_VARIANTS = [
  NotificationPresetVariant.MatchedNoChat,
  NotificationPresetVariant.ResumeChatAfterEvent,
]

const NotificationPresetModal = ({ isOpen, onClose, preset, meta, onSaved, onDeleted }: NotificationPresetModalProps) => {
  const isEdit = preset !== null
  const [name, setName] = useState('')
  const [enabled, setEnabled] = useState(true)
  const [shouldLogSends, setShouldLogSends] = useState(false)
  const [trigger, setTrigger] = useState<NotificationPresetTrigger>(NotificationPresetTrigger.IncompleteOnboarding)
  const [variant, setVariant] = useState<NotificationPresetVariant>(NotificationPresetVariant.LiveCheckins)
  const [delayMinutes, setDelayMinutes] = useState('15')
  const [sortOrder, setSortOrder] = useState('0')
  const [priority, setPriority] = useState('0')
  const [venuePresence, setVenuePresence] = useState<NotificationPresetVenuePresence>(
    NotificationPresetVenuePresence.Any,
  )
  const [messageTemplate, setMessageTemplate] = useState('')
  const [minCount, setMinCount] = useState('')
  const [deepLinkTarget, setDeepLinkTarget] = useState('authResume')
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isEventTrigger = isEventNotificationPresetTrigger(trigger)
  const isVenueTrigger = isVenueNotificationPresetTrigger(trigger)
  const isBatchTrigger = isBatchNotificationPresetTrigger(trigger)
  const isScheduledTrigger = isScheduledNotificationPresetTrigger(trigger)
  const isImmediateTrigger = isImmediateNotificationPresetTrigger(trigger)

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const initialTrigger = preset?.trigger ?? NotificationPresetTrigger.IncompleteOnboarding
    setName(preset?.name ?? '')
    setEnabled(preset?.enabled ?? true)
    setShouldLogSends(preset?.shouldLogSends ?? false)
    setTrigger(initialTrigger)
    setVariant(
      preset?.variant
        ?? (isEventNotificationPresetTrigger(initialTrigger)
          ? NotificationPresetVariant.Default
          : isVenueNotificationPresetTrigger(initialTrigger)
            ? NotificationPresetVariant.PeopleHere
            : isBatchNotificationPresetTrigger(initialTrigger)
              ? NotificationPresetVariant.MatchedNoChat
              : NotificationPresetVariant.LiveCheckins),
    )
    setDelayMinutes(String(preset?.delayMinutes ?? (isImmediateNotificationPresetTrigger(initialTrigger) ? 0 : 15)))
    setSortOrder(String(preset?.sortOrder ?? 0))
    setPriority(String(preset?.priority ?? 0))
    setVenuePresence(preset?.venuePresence ?? NotificationPresetVenuePresence.Any)
    setMessageTemplate(preset?.messageTemplate ?? '')
    setMinCount(preset?.minCount != null ? String(preset.minCount) : '')
    setDeepLinkTarget(
      preset?.deepLinkTarget
        ?? (isScheduledNotificationPresetTrigger(initialTrigger)
          ? (NOTIFICATION_PRESET_DEFAULT_DEEP_LINK_TARGETS[initialTrigger] ?? 'authResume')
          : ''),
    )
    setError(null)
    setIsSaving(false)
    setIsDeleting(false)
  }, [isOpen, preset])

  const handleTriggerChange = (nextTrigger: NotificationPresetTrigger) => {
    setTrigger(nextTrigger)
    if (isEventNotificationPresetTrigger(nextTrigger)) {
      setVariant(NotificationPresetVariant.Default)
      setDelayMinutes('0')
      setMinCount('')
      setDeepLinkTarget('')
      return
    }

    if (isVenueNotificationPresetTrigger(nextTrigger)) {
      setVariant(NotificationPresetVariant.PeopleHere)
      setDelayMinutes('0')
      setDeepLinkTarget('')
      return
    }

    if (isBatchNotificationPresetTrigger(nextTrigger)) {
      setVariant(NotificationPresetVariant.MatchedNoChat)
      setDelayMinutes('0')
      setMinCount('')
      setDeepLinkTarget('')
      return
    }

    setVariant(NotificationPresetVariant.LiveCheckins)
    if (delayMinutes === '0') {
      setDelayMinutes('15')
    }
    if (!deepLinkTarget.trim()) {
      setDeepLinkTarget(NOTIFICATION_PRESET_DEFAULT_DEEP_LINK_TARGETS[nextTrigger] ?? 'authResume')
    }
  }

  const templateVariableHelp = useMemo(
    () => getNotificationPresetTemplateVariableHelp(trigger, variant),
    [trigger, variant],
  )

  const nCountDescription = useMemo(
    () => getNotificationPresetNCountDescription(variant, trigger),
    [variant, trigger],
  )

  const showsMinCount = (isScheduledTrigger || isVenueTrigger) && variantUsesNotificationPresetNCount(variant)

  const checkoutTypeHints = useMemo(() => {
    if (trigger !== NotificationPresetTrigger.VenueCheckedOut || !meta?.checkoutTypes?.length) {
      return []
    }

    return meta.checkoutTypes.map(type => ({
      type,
      label: meta.checkoutTypeLabels?.[type] ?? type,
    }))
  }, [meta, trigger])

  const availableVariants = useMemo(() => {
    if (isScheduledTrigger) {
      return meta?.onboardingVariants ?? FALLBACK_ONBOARDING_VARIANTS
    }
    if (isVenueTrigger) {
      return meta?.venueVariants ?? FALLBACK_VENUE_VARIANTS
    }
    if (isBatchTrigger) {
      return meta?.conversationFollowUpVariants ?? FALLBACK_CONVERSATION_FOLLOWUP_VARIANTS
    }
    return [NotificationPresetVariant.Default]
  }, [isScheduledTrigger, isVenueTrigger, isBatchTrigger, meta])

  const handleClose = () => {
    if (isSaving || isDeleting) {
      return
    }
    onClose()
  }

  const handleDelete = async () => {
    if (!isEdit || !preset || !onDeleted) {
      return
    }
    if (!window.confirm(`Delete notification preset "${preset.name}"?`)) {
      return
    }

    setIsDeleting(true)
    setError(null)
    try {
      await adminService.deleteNotificationPreset(preset.publicId)
      onDeleted(preset.publicId)
      onClose()
    } catch {
      setError('Failed to delete notification preset.')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleSubmit = async () => {
    const trimmedName = name.trim()
    const trimmedMessage = messageTemplate.trim()
    const parsedDelay = Number.parseInt(delayMinutes, 10)
    const parsedSortOrder = Number.parseInt(sortOrder, 10)
    const parsedPriority = Number.parseInt(priority, 10)
    const parsedMinCount = minCount.trim().length > 0 ? Number.parseInt(minCount, 10) : null

    if (!trimmedName) {
      setError('Name is required.')
      return
    }
    if (!trimmedMessage) {
      setError('Message template is required.')
      return
    }
    if (!Number.isFinite(parsedDelay) || parsedDelay < 0) {
      setError('Delay must be a non-negative number of minutes.')
      return
    }
    if (isScheduledTrigger && parsedDelay <= 0) {
      setError('Delay must be greater than zero for onboarding presets.')
      return
    }
    if (isImmediateTrigger && parsedDelay !== 0) {
      setError('Immediate presets must have zero delay.')
      return
    }

    setIsSaving(true)
    setError(null)

    try {
      const payload: CreateNotificationPresetRequestDto | UpdateNotificationPresetRequestDto = {
        name: trimmedName,
        enabled,
        shouldLogSends,
        trigger,
        variant: isEventTrigger ? NotificationPresetVariant.Default : variant,
        delayMinutes: parsedDelay,
        sortOrder: Number.isFinite(parsedSortOrder) ? parsedSortOrder : 0,
        priority: Number.isFinite(parsedPriority) ? parsedPriority : 0,
        venuePresence,
        messageTemplate: trimmedMessage,
        notificationType: NOTIFICATION_PRESET_DEFAULT_NOTIFICATION_TYPE[trigger],
        minCount: isEventTrigger || isBatchTrigger ? null : parsedMinCount,
        deepLinkTarget: deepLinkTarget.trim() || null,
      }

      const saved = isEdit
        ? await adminService.updateNotificationPreset(preset.publicId, payload)
        : await adminService.createNotificationPreset(payload as CreateNotificationPresetRequestDto)

      onSaved(saved)
      onClose()
    } catch {
      setError(isEdit ? 'Failed to save preset.' : 'Failed to create preset.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      className="modal-popup confirm-popup role-modal"
      overlayClassName="modal-overlay"
      closeTimeoutMS={300}
    >
      <div className="modal-content-wrapper">
        <h2>{isEdit ? 'Edit notification preset' : 'Create notification preset'}</h2>
        {isScheduledTrigger ? (
          <p className="roles-page-intro" style={{ marginTop: 0 }}>
            Delay is measured from signup start. Current delay preview: {formatDelay(Number.parseInt(delayMinutes, 10) || 0)}.
          </p>
        ) : isBatchTrigger ? (
          <p className="roles-page-intro" style={{ marginTop: 0 }}>
            This preset is evaluated daily at 9:00 by the conversation follow-up cron.
          </p>
        ) : (
          <p className="roles-page-intro" style={{ marginTop: 0 }}>
            This preset sends immediately when the event occurs.
          </p>
        )}

        <form className="modal-body-content" onSubmit={e => e.preventDefault()}>
          <TextInput required type="text" label="Name" name="presetName" value={name} onChange={setName} />

          <div className="form-group">
            <label>
              <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} /> Enabled
            </label>
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={shouldLogSends}
                onChange={e => setShouldLogSends(e.target.checked)}
              />{' '}
              Log sends
            </label>
            <p className="roles-page-intro" style={{ marginTop: '0.25rem', marginBottom: 0 }}>
              When enabled, each successful send is written to server logs with preset, user, and message details.
            </p>
          </div>

          <div className="form-group">
            <label>Trigger</label>
            <select
              value={trigger}
              onChange={e => handleTriggerChange(e.target.value as NotificationPresetTrigger)}
            >
              {(meta?.triggers ?? [NotificationPresetTrigger.IncompleteOnboarding]).map(value => (
                <option key={value} value={value}>
                  {meta?.triggerLabels[value] ?? value}
                </option>
              ))}
            </select>
          </div>

          {(isScheduledTrigger || isVenueTrigger || isBatchTrigger) && (
            <div className="form-group">
              <label>Variant</label>
              <select value={variant} onChange={e => setVariant(e.target.value as NotificationPresetVariant)}>
                {availableVariants.map(value => (
                  <option key={value} value={value}>
                    {meta?.variantLabels[value] ?? value}
                  </option>
                ))}
              </select>
            </div>
          )}

          {isScheduledTrigger && (
            <div className="form-group">
              <label>Delay after signup (minutes)</label>
              <input type="number" min={1} value={delayMinutes} onChange={e => setDelayMinutes(e.target.value)} />
            </div>
          )}

          <div className="form-group">
            <label>Sort order</label>
            <input type="number" value={sortOrder} onChange={e => setSortOrder(e.target.value)} />
          </div>

          <div className="form-group">
            <label>Priority</label>
            <input type="number" value={priority} onChange={e => setPriority(e.target.value)} />
            <p className="roles-page-intro" style={{ marginTop: '0.5rem' }}>
              When several presets share a trigger, higher priority wins. We pick the first preset in descending priority
              order whose minimum count condition passes.
            </p>
          </div>

          {(isScheduledTrigger || isVenueTrigger) && (
            <div className="form-group">
              <label>Venue presence</label>
              <select
                value={venuePresence}
                onChange={e => setVenuePresence(e.target.value as NotificationPresetVenuePresence)}
              >
                {(meta?.venuePresenceValues ?? Object.values(NotificationPresetVenuePresence)).map(value => (
                  <option key={value} value={value}>
                    {meta?.venuePresenceLabels?.[value] ?? value}
                  </option>
                ))}
              </select>
              <p className="roles-page-intro" style={{ marginTop: '0.5rem' }}>
                The recipient must have this check-in state relative to the venue referenced by the notification.
                If it does not match, the next eligible preset by priority is evaluated.
              </p>
            </div>
          )}

          <div className="form-group">
            <label>Message template</label>
            <textarea rows={5} value={messageTemplate} onChange={e => setMessageTemplate(e.target.value)} />
            {templateVariableHelp.length > 0 && (
              <div className="roles-page-intro" style={{ marginTop: '0.5rem' }}>
                <p style={{ margin: '0 0 0.5rem' }}>Available variables:</p>
                <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                  {templateVariableHelp.map(({ variable, description }) => (
                    <li key={variable}>
                      <code>{variable}</code> — {description}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {checkoutTypeHints.length > 0 && (
              <p className="roles-page-intro" style={{ marginTop: '0.5rem' }}>
                {'{{checkoutType}}'} values:{' '}
                {checkoutTypeHints.map(({ type, label }) => `${type} (${label})`).join(', ')}
              </p>
            )}
          </div>

          {showsMinCount && (
            <div className="form-group">
              <label>Minimum count (optional)</label>
              <input
                type="number"
                min={0}
                value={minCount}
                onChange={e => setMinCount(e.target.value)}
                placeholder="Skip send when {{n}} is below this value"
              />
              {nCountDescription && (
                <p className="roles-page-intro" style={{ marginTop: '0.5rem' }}>
                  {'{{n}}'} counts: {nCountDescription} If set, minimum count skips this preset when {'{{n}}'} is
                  below that threshold.
                </p>
              )}
            </div>
          )}

          <div className="form-group">
            <label>Deep link target (optional)</label>
            <select value={deepLinkTarget} onChange={e => setDeepLinkTarget(e.target.value)}>
              <option value="">— Default for trigger —</option>
              {(meta?.deepLinkTargets ?? []).map(target => (
                <option key={target} value={target}>
                  {meta?.deepLinkTargetLabels?.[target] ?? target}
                </option>
              ))}
            </select>
            <p className="roles-page-intro" style={{ marginTop: '0.5rem' }}>
              Sent as <code>data.deepLinkTarget</code>. Navigation IDs go in <code>data.extraData</code>.
            </p>
          </div>
        </form>

        {error && (
          <div className="page-error" style={{ margin: 0 }}>
            {error}
          </div>
        )}

        <div className="modal-footer-content">
          {isEdit && onDeleted && (
            <button
              type="button"
              className="styled-button"
              onClick={() => void handleDelete()}
              disabled={isSaving || isDeleting}
              style={{ marginRight: 'auto' }}
            >
              {isDeleting ? 'Deleting…' : 'Delete'}
            </button>
          )}
          <button type="button" className="styled-button" onClick={handleClose} disabled={isSaving || isDeleting}>
            Cancel
          </button>
          <button type="button" className="styled-button" onClick={() => void handleSubmit()} disabled={isSaving || isDeleting}>
            {isSaving ? 'Saving…' : isEdit ? 'Save changes' : 'Create preset'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default NotificationPresetModal
